// ABOUTME: Content component for nutrient hint tooltips
// ABOUTME: Shows description, health impact, sources and deficit/excess info

import { useTranslation } from "react-i18next"
import { memo } from "react"
import DOMPurify from "dompurify"

// Группируем связанные поля в отдельные интерфейсы
interface NutritionalInfo {
  title?: string
  description?: string
  forSport?: string
  exampleContains?: string
}

interface HealthImpact {
  deficitOrOversupply?: string
  atDeficiter?: string
  inExcess?: string
  howReplaced?: string
}

interface SourceInfo {
  safeSource?: string
  dangSource?: string
  riskFoods?: string
  sweatLoss?: string
}

export interface HintContent {
  nutritionalInfo?: NutritionalInfo
  healthImpact?: HealthImpact
  sourceInfo?: SourceInfo
  // Для обратной совместимости
  title?: string
  description?: string
  forSport?: string
  exampleContains?: string
  atDeficiter?: string
  inExcess?: string
  deficitOrOversupply?: string
  howReplaced?: string
  sweatLoss?: string
  safeSource?: string
  dangSource?: string
  riskFoods?: string
}

interface HintDataContentProps {
  content: HintContent
}

// Вспомогательный компонент для отображения одного поля подсказки
const HintField = memo(
  ({
    label,
    value,
    color = "text-[#7C7C7C]",
    useHtml = false,
    marginBottom = true,
  }: {
    label: string
    value: string
    color?: string
    useHtml?: boolean
    marginBottom?: boolean
  }) => {
    if (!value) return null

    return (
      <div className={`leading-[16px] text-[12px] whitespace-normal ${marginBottom ? "mb-2" : ""}`}>
        <span className={`block ${color}`}>{label}</span>
        {useHtml ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }} /> : value}
      </div>
    )
  },
)

// Разделительная линия
const Divider = memo(() => <div className="my-2 h-[1px] w-[80%] bg-[#5A5A5A]" />)

export const HintDataContent = memo(({ content }: HintDataContentProps) => {
  const { t } = useTranslation()

  // Объединяем старый и новый форматы данных
  const { nutritionalInfo = {}, healthImpact = {}, sourceInfo = {}, ...legacyContent } = content

  const combinedContent = {
    ...nutritionalInfo,
    ...healthImpact,
    ...sourceInfo,
    ...legacyContent,
  }

  return (
    <div className="max-w-[262px] min-w-[220px] overflow-hidden max-[420px]:max-w-[220px] max-h-fit text-[13px] text-white leading-[16px] pb-2">
      {combinedContent.title && (
        <>
          <div className="font-semibold whitespace-normal">{combinedContent.title}</div>
          <Divider />
        </>
      )}

      {combinedContent.description && (
        <div
          className="mb-2 whitespace-normal"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(combinedContent.description) }}
        />
      )}

      {combinedContent.forSport && (
        <>
          <HintField
            label={t("common.forSport")}
            value={combinedContent.forSport}
            useHtml
            marginBottom={false}
          />
          <Divider />
        </>
      )}

      <HintField
        label={t("common.exampleContains")}
        value={combinedContent.exampleContains || ""}
        useHtml
      />
      <HintField
        label={t("common.howReplaced")}
        value={combinedContent.howReplaced || ""}
      />
      <HintField
        label={t("hint.sweatLoss")}
        value={combinedContent.sweatLoss || ""}
        useHtml
      />
      <HintField
        label={t("hint.safeSources")}
        value={combinedContent.safeSource || ""}
      />
      <HintField
        label={t("hint.dangSources")}
        value={combinedContent.dangSource || ""}
      />
      <HintField
        label={t("hint.riskFoods")}
        value={combinedContent.riskFoods || ""}
      />

      <HintField
        label={t("common.deficitOrOversupply")}
        value={combinedContent.deficitOrOversupply || ""}
        color="text-[#E95151]"
        marginBottom={!!combinedContent.atDeficiter}
      />
      <HintField
        label={t("common.atDeficit")}
        value={combinedContent.atDeficiter || ""}
        color="text-[#E95151]"
        marginBottom={!!combinedContent.inExcess}
        useHtml
      />
      <HintField
        label={t("common.inExcess")}
        value={combinedContent.inExcess || ""}
        color="text-[#E95151]"
        marginBottom={false}
        useHtml
      />
    </div>
  )
})
